import { reportsApi } from '../api/services';
import { Alert, Badge, Card, Loadable, PageHeader, Stat, Table, entryTone, fmtDate, fmtMoney, useAsync } from '../ui/components';
import { Link } from '../router';
import { ENTRY_STATUS } from '../api/types';
import { PageHelp } from '../ui/help';

export function DashboardPage() {
  const state = useAsync(() => reportsApi.dashboard(), []);
  return (
    <>
      <PageHeader title="Resumen contable" subtitle={state.data ? `${state.data.company} · al ${fmtDate(state.data.asOf)}` : undefined} />
      <PageHelp page="dashboard" />
      <Loadable state={state}>
        {(d) => {
          const max = Math.max(1, ...d.last6Months.flatMap((m) => [m.income, m.expense]));
          return (
            <>
              {d.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
              <div className="acc-stats">
                <Stat label="Período abierto" value={d.openPeriod ?? '—'} />
                <Stat label="Asientos abiertos" value={<>{d.openEntries}{d.unbalancedOpenEntries > 0 && <small> ({d.unbalancedOpenEntries} sin cuadrar)</small>}</>} />
                <Stat label="Ingresos del mes" value={fmtMoney(d.monthIncome)} />
                <Stat label="Costos y gastos del mes" value={fmtMoney(d.monthExpense)} />
                <Stat label="Resultado del mes" value={fmtMoney(d.monthResult)} tone={d.monthResult >= 0 ? 'pos' : 'neg'} />
                <Stat label="Cuentas por cobrar" value={fmtMoney(d.receivables)} />
                <Stat label="Cuentas por pagar" value={fmtMoney(d.payables)} />
                <Stat label="Saldo contable en bancos" value={fmtMoney(d.bankBalance)} />
              </div>
              <div className="acc-grid-2">
                <Card title="Ingresos y gastos — últimos 6 meses">
                  <div className="acc-bars" role="img" aria-label="Gráfico de ingresos y gastos de los últimos seis meses">
                    {d.last6Months.map((m) => (
                      <div key={m.month} className="acc-bar-group">
                        <div className="acc-bar-pair">
                          <div className="acc-bar acc-bar-in" style={{ height: `${(Math.max(0, m.income) / max) * 100}%` }} title={`Ingresos ${fmtMoney(m.income)}`} />
                          <div className="acc-bar acc-bar-out" style={{ height: `${(Math.max(0, m.expense) / max) * 100}%` }} title={`Gastos ${fmtMoney(m.expense)}`} />
                        </div>
                        <span>{m.month}</span>
                      </div>
                    ))}
                  </div>
                  <div className="acc-legend"><span className="acc-dot acc-bar-in" /> Ingresos <span className="acc-dot acc-bar-out" /> Costos y gastos</div>
                </Card>
                <Card title="Últimos asientos" actions={<Link to="/entries">Ver todos</Link>}>
                  <Table rows={d.recentEntries} rowKey={(r) => r.entryCode} columns={[
                    { key: 'c', title: 'Asiento', render: (r) => <Link to={`/entries/${encodeURIComponent(r.entryCode)}`}>{r.entryCode}</Link> },
                    { key: 'f', title: 'Fecha', render: (r) => fmtDate(r.date) },
                    { key: 'd', title: 'Descripción', render: (r) => r.description },
                    { key: 's', title: 'Estado', render: (r) => <Badge tone={entryTone(r.status)}>{ENTRY_STATUS[r.status] ?? r.status}</Badge> },
                    { key: 't', title: 'Total', align: 'right', render: (r) => fmtMoney(r.total) },
                  ]} />
                </Card>
              </div>
            </>
          );
        }}
      </Loadable>
    </>
  );
}
